/**
 * Loan Cars — two sections:
 *   Fleet       — loan car register with status changes + add-car modal
 *   Agreements  — loan-out ledger with overdue highlighting, new-loan
 *                 modal (from AVAILABLE cars) and a return modal.
 */
import { useMemo, useState } from 'react';
import { formatInOrgTz } from '../lib/datetime';
import TopBar from '../components/TopBar';
import FleetTabs from '../components/FleetTabs';
import LookupField from '../components/LookupField';
import { loanCarsService } from '../lib/services';
import { useApi } from '../lib/hooks/useApi';
import { useToast } from '../lib/toast';
import { apiErrorMessage } from '../lib/apiError';
import { useCustomerOptions, useVehicleOptions } from '../lib/useDirectory';
import { useDirectoryNames } from '../lib/useDirectoryNames';

const CAR_STATUSES = ['AVAILABLE', 'ON_LOAN', 'MAINTENANCE', 'RETIRED'];
const CAR_STATUS_VAR = {
  AVAILABLE:   'var(--success)',
  ON_LOAN:     'var(--warning)',
  MAINTENANCE: 'var(--info)',
  RETIRED:     'var(--text-tertiary)',
};
const AGREEMENT_TONE = { OPEN: 'warning', RETURNED: 'success', OVERDUE: 'danger' };

function fmtWhen(iso) {
  return formatInOrgTz(iso, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function asList(d) {
  if (Array.isArray(d)) return d;
  return d?.content ?? [];
}
function isOverdue(a) {
  return a.status !== 'RETURNED' && a.dueBackAt && new Date(a.dueBackAt).getTime() < Date.now();
}

/* ── mocks ── */
const MOCK_CARS = [
  { id: 'lc-1', vehicleId: 'veh-201', vehicleLabel: '2023 Toyota Corolla Ascent', status: 'AVAILABLE',   notes: 'Service loaner' },
  { id: 'lc-2', vehicleId: 'veh-202', vehicleLabel: '2022 Toyota Camry Ascent',   status: 'ON_LOAN',     notes: null },
  { id: 'lc-3', vehicleId: 'veh-203', vehicleLabel: '2023 Toyota RAV4 GX',        status: 'MAINTENANCE', notes: '60k service' },
  { id: 'lc-4', vehicleId: 'veh-204', vehicleLabel: '2020 Toyota Yaris SX',       status: 'RETIRED',     notes: 'To be wholesaled' },
];
const MOCK_AGREEMENTS = [
  {
    id: 'la-1', loanCarId: 'lc-2', carLabel: '2022 Toyota Camry Ascent',
    customerId: 'cust-45', customerName: 'Liam O’Brien',
    outAt: new Date(Date.now() - 2 * 86400e3).toISOString(),
    dueBackAt: new Date(Date.now() - 4 * 3600e3).toISOString(), // overdue
    kmOut: 41200, fuelOut: 'FULL', status: 'OPEN',
  },
  {
    id: 'la-2', loanCarId: 'lc-1', carLabel: '2023 Toyota Corolla Ascent',
    customerId: 'cust-77', customerName: 'Ava Taylor',
    outAt: new Date(Date.now() - 6 * 86400e3).toISOString(),
    dueBackAt: new Date(Date.now() - 4 * 86400e3).toISOString(),
    returnedAt: new Date(Date.now() - 4 * 86400e3).toISOString(),
    kmOut: 12800, kmIn: 12988, fuelOut: 'FULL', fuelIn: '3/4', status: 'RETURNED',
  },
];

/* ── add car modal ── */
function AddCarModal({ onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ vehicleId: '', notes: '' });
  const [busy, setBusy] = useState(false);

  const vehiclesQ = useVehicleOptions();

  const canSubmit = !busy && !!form.vehicleId;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await loanCarsService.create({ vehicleId: form.vehicleId, notes: form.notes.trim() || null });
      toast('Loan car added to fleet', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`Add failed: ${apiErrorMessage(err)}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(440px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">Add loan car</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <LookupField
            id="lc-vehicle"
            label="Vehicle"
            required
            placeholder="Search by model, stock # or VIN…"
            emptyHint="No vehicle matches that search"
            options={vehiclesQ.options}
            loading={vehiclesQ.loading}
            value={form.vehicleId || null}
            onChange={(id) => setForm((f) => ({ ...f, vehicleId: id || '' }))}
          />
          <div className="form-field">
            <label>Notes</label>
            <textarea rows={3} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Service loaner, no smoking…" />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Adding…' : 'Add car'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── new loan modal ── */
function NewLoanModal({ cars, onClose, onCreated }) {
  const { toast } = useToast();
  const [form, setForm] = useState({
    loanCarId: '', customerId: '', dueBackAt: '', kmOut: '', fuelOut: 'FULL', conditionOutNotes: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const customersQ = useCustomerOptions();

  const available = useMemo(() => cars.filter((c) => c.status === 'AVAILABLE'), [cars]);

  // The loan car comes from the fleet, not from stock — so it stays a picker
  // over the available fleet rather than a vehicle lookup.
  const carOptions = useMemo(() => available.map((c) => ({
    id: c.id,
    primary: c._vehicle || c.vehicleLabel || `Loan car ${String(c.id).slice(0, 8)}`,
    secondary: [c.rego, c.km != null ? `${Number(c.km).toLocaleString()} km` : null]
      .filter(Boolean).join(' · ') || null,
    search: `${c._vehicle || c.vehicleLabel || ''} ${c.rego || ''} ${c.id}`.toLowerCase(),
  })), [available]);

  const canSubmit = !busy && !!form.loanCarId && !!form.customerId;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    try {
      await loanCarsService.createAgreement({
        loanCarId: form.loanCarId,
        customerId: form.customerId,
        dueBackAt: form.dueBackAt ? new Date(form.dueBackAt).toISOString() : null,
        kmOut: form.kmOut === '' ? null : Number(form.kmOut),
        fuelOut: form.fuelOut || null,
        conditionOutNotes: form.conditionOutNotes.trim() || null,
      });
      toast('Loan agreement created', { variant: 'success' });
      onCreated();
      onClose();
    } catch (err) {
      toast(`Create failed: ${apiErrorMessage(err)}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(520px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div className="modal-title">New loan agreement</div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <LookupField
            id="loan-car"
            label="Loan car (available only)"
            required
            placeholder="Search the available fleet…"
            emptyHint={available.length === 0
              ? 'No cars are currently AVAILABLE'
              : 'No car matches that search'}
            options={carOptions}
            value={form.loanCarId || null}
            onChange={(id) => set('loanCarId', id || '')}
          />

          <LookupField
            id="loan-customer"
            label="Customer"
            required
            placeholder="Search by name, phone or email…"
            emptyHint="No customer matches that search"
            options={customersQ.options}
            loading={customersQ.loading}
            value={form.customerId || null}
            onChange={(id) => set('customerId', id || '')}
          />

          <div className="form-field">
            <label>Due back</label>
            <input type="datetime-local" value={form.dueBackAt} onChange={(e) => set('dueBackAt', e.target.value)} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label>KM out</label>
              <input type="number" min={0} value={form.kmOut} onChange={(e) => set('kmOut', e.target.value)} placeholder="41200" />
            </div>
            <div className="form-field">
              <label>Fuel out</label>
              <select value={form.fuelOut} onChange={(e) => set('fuelOut', e.target.value)}>
                {['FULL', '3/4', '1/2', '1/4', 'EMPTY'].map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="form-field">
            <label>Condition out (notes)</label>
            <textarea rows={2} value={form.conditionOutNotes} onChange={(e) => set('conditionOutNotes', e.target.value)} placeholder="Existing scuff rear bumper…" />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={!canSubmit}>
            {busy ? 'Creating…' : 'Create loan'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── return modal ── */
function ReturnModal({ agreement, onClose, onReturned }) {
  const { toast } = useToast();
  const [form, setForm] = useState({ kmIn: '', fuelIn: 'FULL', conditionInNotes: '' });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  if (!agreement) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await loanCarsService.returnAgreement(agreement.id, {
        kmIn: form.kmIn === '' ? null : Number(form.kmIn),
        fuelIn: form.fuelIn || null,
        conditionInNotes: form.conditionInNotes.trim() || null,
      });
      toast('Loan car returned', { variant: 'success' });
      onReturned();
      onClose();
    } catch (err) {
      toast(`Return failed: ${err?.response?.data?.message || err?.message || 'unknown error'}`, { variant: 'danger' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={onClose}>
      <form className="modal" style={{ width: 'min(460px, 92vw)' }} onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Return loan car</div>
            <div className="mono text-xs text-muted">{agreement._car || agreement.carLabel || 'Loan car'} · {agreement._customer || '—'}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>
        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-field">
              <label>KM in *</label>
              <input type="number" min={0} value={form.kmIn} onChange={(e) => set('kmIn', e.target.value)} autoFocus required />
            </div>
            <div className="form-field">
              <label>Fuel in</label>
              <select value={form.fuelIn} onChange={(e) => set('fuelIn', e.target.value)}>
                {['FULL', '3/4', '1/2', '1/4', 'EMPTY'].map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
            </div>
          </div>
          <div className="form-field">
            <label>Condition in (notes)</label>
            <textarea rows={2} value={form.conditionInNotes} onChange={(e) => set('conditionInNotes', e.target.value)} placeholder="New chip on windscreen…" />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn small" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn small primary" disabled={busy || form.kmIn === ''}>
            {busy ? 'Returning…' : 'Complete return'}
          </button>
        </div>
      </form>
    </div>
  );
}

/* ── page ── */
export default function LoanCars() {
  const { toast } = useToast();
  const [addCarOpen, setAddCarOpen] = useState(false);
  const [newLoanOpen, setNewLoanOpen] = useState(false);
  const [returning, setReturning] = useState(null);

  const carsQ = useApi(() => loanCarsService.list({ page: 0, limit: 100 }), { devFallback: MOCK_CARS });
  const agreementsQ = useApi(() => loanCarsService.agreements({ page: 0, limit: 100 }), { devFallback: MOCK_AGREEMENTS });

  const names = useDirectoryNames();

  // Resolve the vehicle behind each loan car…
  const cars = useMemo(() => asList(carsQ.data).map((c) => ({
    ...c,
    _vehicle: names.vehicle(c.vehicleId, c.vehicleLabel, { dash: null }),
  })), [carsQ.data, names]);

  // …then an agreement's car resolves through the loan-car row to that vehicle,
  // and its customer resolves by id.
  const carsById = useMemo(() => new Map(cars.map((c) => [c.id, c])), [cars]);
  const agreements = useMemo(() => asList(agreementsQ.data).map((a) => {
    const car = carsById.get(a.loanCarId);
    return {
      ...a,
      _customer: names.customer(a.customerId, a.customerName, { dash: null }),
      _car: a.carLabel || car?._vehicle || null,
    };
  }), [agreementsQ.data, carsById, names]);

  const stats = useMemo(() => ({
    available: cars.filter((c) => c.status === 'AVAILABLE').length,
    onLoan: cars.filter((c) => c.status === 'ON_LOAN').length,
    overdue: agreements.filter(isOverdue).length,
  }), [cars, agreements]);

  const changeCarStatus = async (car, status) => {
    try {
      await loanCarsService.update(car.id, { status });
      toast(`${car._vehicle || car.vehicleLabel || 'Loan car'} → ${status.replace('_', ' ')}`, { variant: 'success' });
      carsQ.refetch();
    } catch (e) {
      toast(`Status change failed: ${e?.response?.data?.message || e?.message || 'unknown error'}`, { variant: 'danger' });
    }
  };

  const refetchBoth = () => { carsQ.refetch(); agreementsQ.refetch(); };

  return (
    <>
      <TopBar
        crumbs={['Vehicles out', 'Loan cars']}
        right={
          <>
            <button className="btn small" onClick={refetchBoth}>↻ Refresh</button>
            <button className="btn small" onClick={() => setAddCarOpen(true)}>+ Add car</button>
            <button className="btn small primary" onClick={() => setNewLoanOpen(true)}>+ New loan</button>
          </>
        }
      />
      <div className="app-content" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FleetTabs />

        {/* Snapshot */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
          <div className="card"><div className="card-label">Available</div><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--success)' }}>{stats.available}</div></div>
          <div className="card"><div className="card-label">On loan</div><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--warning)' }}>{stats.onLoan}</div></div>
          <div className="card"><div className="card-label">Overdue returns</div><div style={{ fontSize: 24, fontWeight: 800, color: 'var(--danger)' }}>{stats.overdue}</div></div>
        </div>

        {/* Fleet */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Fleet</div>
              <div className="mono text-xs text-muted">Courtesy &amp; service loan vehicles</div>
            </div>
            {carsQ.loading && <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>loading…</span>}
          </div>
          {cars.length === 0 ? (
            <div className="empty-state" style={{ padding: 32 }}>
              <div className="icon">🚗</div>
              <div className="title">No loan cars</div>
              <div className="desc">Add a vehicle to the loan fleet to get started.</div>
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr><th>Vehicle</th><th>Status</th><th>Notes</th><th style={{ width: 180 }}>Change status</th></tr>
              </thead>
              <tbody>
                {cars.map((c) => (
                  <tr key={c.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{c._vehicle || c.vehicleLabel || 'Loan car'}</div>
                      {(c.rego || c.dmsStockId) && (
                        <div className="mono text-xs text-muted">{c.rego || `Stock ${c.dmsStockId}`}</div>
                      )}
                    </td>
                    <td>
                      <span className="pill filled" style={{ color: CAR_STATUS_VAR[c.status] || 'var(--text-secondary)' }}>
                        {(c.status || '—').replace('_', ' ')}
                      </span>
                    </td>
                    <td className="text-muted">{c.notes || '—'}</td>
                    <td>
                      <select
                        className="btn small"
                        value={c.status || 'AVAILABLE'}
                        onChange={(e) => changeCarStatus(c, e.target.value)}
                        aria-label="Change loan car status"
                      >
                        {CAR_STATUSES.map((s) => <option key={s} value={s}>{s.replace('_', ' ')}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          )}
        </div>

        {/* Agreements */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center' }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Agreements</div>
              <div className="mono text-xs text-muted">Loan-out ledger with due-back tracking</div>
            </div>
            {agreementsQ.loading && <span className="mono text-xs text-muted" style={{ marginLeft: 'auto' }}>loading…</span>}
          </div>
          {agreements.length === 0 ? (
            <div className="empty-state" style={{ padding: 32 }}>
              <div className="icon">📄</div>
              <div className="title">No agreements</div>
              <div className="desc">Loan a car to a customer to create the first agreement.</div>
            </div>
          ) : (
            <div className="table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Customer</th><th>Car</th><th>Out</th><th>Due back</th><th>KM out / in</th><th>Status</th><th style={{ width: 110 }}></th>
                </tr>
              </thead>
              <tbody>
                {agreements.map((a) => {
                  const overdue = isOverdue(a);
                  const status = a.status || (a.returnedAt ? 'RETURNED' : 'OPEN');
                  return (
                    <tr key={a.id}>
                      <td>
                        <div style={{ fontWeight: 600 }}>{a._customer || '—'}</div>
                      </td>
                      <td>{a._car || '—'}</td>
                      <td>{fmtWhen(a.outAt)}</td>
                      <td style={{ color: overdue ? 'var(--danger)' : 'var(--text-secondary)', fontWeight: overdue ? 600 : 400 }}>
                        {fmtWhen(a.dueBackAt)}{overdue ? ' ⚠' : ''}
                      </td>
                      <td className="mono text-xs">
                        {a.kmOut != null ? a.kmOut.toLocaleString('en-AU') : '—'} / {a.kmIn != null ? a.kmIn.toLocaleString('en-AU') : '—'}
                      </td>
                      <td>
                        <span className={`pill filled ${AGREEMENT_TONE[overdue ? 'OVERDUE' : status] || ''}`}>
                          {overdue && status !== 'RETURNED' ? 'OVERDUE' : status}
                        </span>
                      </td>
                      <td>
                        {status !== 'RETURNED' && (
                          <button className="btn small primary" onClick={() => setReturning(a)}>Return</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
        </div>
      </div>

      {/* Mounted only while open — their lookups fetch on mount. */}
      {addCarOpen && (
        <AddCarModal onClose={() => setAddCarOpen(false)} onCreated={carsQ.refetch} />
      )}
      {newLoanOpen && (
        <NewLoanModal cars={cars} onClose={() => setNewLoanOpen(false)} onCreated={refetchBoth} />
      )}
      <ReturnModal agreement={returning} onClose={() => setReturning(null)} onReturned={refetchBoth} />
    </>
  );
}
